import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * NotesList - Displays list of existing notes with edit/delete actions
 * @param {Array} notes - Array of note objects
 * @param {number} editingNoteIndex - Index of note being edited (null if none)
 * @param {Function} onEditNote - Callback when note is clicked for editing
 * @param {Function} onDeleteNote - Callback when delete is clicked
 * @param {boolean} saving - Whether an operation is in progress
 * @param {string} borderColor - Border color for notes (defaults to blue)
 */
export default function NotesList({
  notes,
  editingNoteIndex,
  onEditNote,
  onDeleteNote,
  saving,
  borderColor = 'rgba(25, 118, 210, 0.5)'
}) {
  if (notes.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <strong>Notes ({notes.length}):</strong>
      </Typography>
      {[...notes].reverse().map((note, reverseIndex) => {
        const index = notes.length - 1 - reverseIndex;
        if (!note || !note.text) return null;

        const isEditing = editingNoteIndex === index;

        return (
          <Box
            key={index}
            sx={{
              mt: 1,
              p: 1.5,
              backgroundColor: isEditing ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: 1,
              borderLeft: isEditing ? '3px solid rgba(25, 118, 210, 0.8)' : `3px solid ${borderColor}`,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.05)',
                transform: 'translateX(4px)'
              },
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            <Box
              onClick={() => onEditNote(index)}
              sx={{ flex: 1 }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {note.text}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {note.updatedAt ? `Updated: ${new Date(note.updatedAt).toLocaleString()}` : new Date(note.createdAt).toLocaleString()}
                {note.location && ` • ${note.location.latitude.toFixed(6)}, ${note.location.longitude.toFixed(6)}`}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteNote(index);
              }}
              disabled={saving}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.08)'
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
}
